// Smart Daara - Physical school: students (records, enrollment, CSV import)
import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { physicalAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { UserPlus, Upload } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = { email: '', password: '', first_name: '', last_name: '', class_id: '', birth_date: '', gender: '', phone: '' };

export default function PhysicalStudents() {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [filterClass, setFilterClass] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [importReport, setImportReport] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    const [s, c] = await Promise.all([physicalAPI.listStudents(filterClass || undefined), physicalAPI.listClasses()]);
    setStudents(s.data); setClasses(c.data);
  };
  useEffect(() => { load().catch(() => {}); }, [filterClass]);

  const save = async () => {
    try {
      if (editId) {
        await physicalAPI.updateStudent(editId, {
          first_name: form.first_name, last_name: form.last_name, phone: form.phone,
          class_id: form.class_id || null, birth_date: form.birth_date, gender: form.gender,
        });
        toast.success(fr ? 'Élève mis à jour' : 'Student updated');
      } else {
        if (!form.email || !form.password || !form.first_name) {
          toast.error(fr ? 'Email, mot de passe et prénom requis' : 'Email, password and first name required'); return;
        }
        const res = await physicalAPI.createStudent({ ...form, class_id: form.class_id || null });
        toast.success(`${fr ? 'Élève créé — matricule' : 'Student created — ID'} ${res.data.matricule}`);
      }
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await physicalAPI.importStudentsCsv(fd);
      setImportReport(res.data);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur import'); }
    e.target.value = '';
  };

  return (
    <div className="space-y-6" data-testid="physical-students-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-outfit font-bold text-slate-900">{fr ? 'Élèves' : 'Students'}</h1>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={onImport} />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-csv-btn">
            <Upload className="w-4 h-4 mr-2" />{fr ? 'Import CSV' : 'CSV import'}
          </Button>
          <Button className="bg-emerald-700" onClick={() => { setEditId(null); setForm(EMPTY); setOpen(true); }} data-testid="add-student-btn">
            <UserPlus className="w-4 h-4 mr-2" />{fr ? 'Nouvel élève' : 'New student'}
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-400 -mt-4">
        {fr ? 'Modèle CSV : email,password,first_name,last_name,class_name,birth_date,gender,phone'
            : 'CSV template: email,password,first_name,last_name,class_name,birth_date,gender,phone'}
      </p>

      <div className="flex items-center gap-2">
        <Label className="text-sm">{fr ? 'Classe :' : 'Class:'}</Label>
        <select className="h-9 border border-slate-200 rounded-md px-2 text-sm" value={filterClass} onChange={(e) => setFilterClass(e.target.value)} data-testid="student-class-filter">
          <option value="">{fr ? 'Toutes' : 'All'}</option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="space-y-2">
        {students.map((s) => (
          <Card key={s.user_id} className="border-0 shadow-sm">
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-medium">{s.first_name} {s.last_name}</p>
                <p className="text-xs text-slate-400">{s.matricule} · {s.email}</p>
              </div>
              <div className="flex items-center gap-3">
                {s.class_name && <Badge className="bg-emerald-100 text-emerald-700">{s.class_name}</Badge>}
                <Button size="sm" variant="outline" onClick={() => {
                  setEditId(s.user_id);
                  setForm({ ...EMPTY, first_name: s.first_name, last_name: s.last_name, phone: s.phone || '', class_id: s.class_id || '', birth_date: s.birth_date || '', gender: s.gender || '' });
                  setOpen(true);
                }} data-testid={`edit-student-${s.user_id}`}>{fr ? 'Modifier' : 'Edit'}</Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {students.length === 0 && <p className="text-slate-400 text-sm text-center py-6">{fr ? 'Aucun élève' : 'No students'}</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? (fr ? 'Modifier l\'élève' : 'Edit student') : (fr ? 'Nouvel élève' : 'New student')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!editId && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="student-email-input" /></div>
                <div><Label>{fr ? 'Mot de passe' : 'Password'}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="student-password-input" /></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{fr ? 'Prénom' : 'First name'}</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="student-firstname-input" /></div>
              <div><Label>{fr ? 'Nom' : 'Last name'}</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{fr ? 'Classe' : 'Class'}</Label>
                <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={form.class_id} onChange={(e) => setForm({ ...form, class_id: e.target.value })} data-testid="student-class-select">
                  <option value="">—</option>
                  {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div><Label>{fr ? 'Date de naissance' : 'Birth date'}</Label><Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{fr ? 'Genre' : 'Gender'}</Label>
                <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                  <option value="">—</option><option value="M">M</option><option value="F">F</option>
                </select>
              </div>
              <div><Label>{fr ? 'Téléphone' : 'Phone'}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button className="bg-emerald-700" onClick={save} data-testid="save-student-btn">{fr ? 'Enregistrer' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!importReport} onOpenChange={() => setImportReport(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{fr ? 'Rapport d\'import' : 'Import report'}</DialogTitle></DialogHeader>
          <p className="text-emerald-700 font-medium">{importReport?.created} {fr ? 'élève(s) créé(s)' : 'student(s) created'}</p>
          {importReport?.errors?.length > 0 && (
            <div className="max-h-60 overflow-y-auto space-y-1">
              {importReport.errors.map((e, i) => (
                <p key={i} className="text-sm text-red-600">{fr ? 'Ligne' : 'Line'} {e.line} : {e.error}</p>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
